import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Breadcrumb, IBreadcrumbItem, MessageBar, MessageBarButton, MessageBarType } from "@fluentui/react";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { resetContentManagerDirectory, setContentManagerExchange, setContentManagerFilterValue, setContentManagerMoveTo, setContentManagerSearchValue, toggleContentManagerReload } from "../../src/taskpane/redux/modules/contentManagerDirectory";
import { removeDirectoriesByIndex, resetWorkingDirectories } from "../../src/taskpane/redux/modules/workingDirectory";
import { ContentManagerCopyMoveModeEnum } from "../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum";
import { setFiles } from "../../src/taskpane/redux/modules/file";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { DroppableBreadcrumbItem } from "../../src/taskpane/components/_v2/components/DroppableBreadcrumbItem/DroppableBreadcrumbItem";

export interface IContentBodyBreadcrumbProps {
}

export const ContentBodyBreadcrumb = ({ }: IContentBodyBreadcrumbProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [breadCrumbItems, setBreadCrumbItems] = React.useState<IBreadcrumbItem[]>([]);

    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const searchValue = useSelector((state: RootState) => state.contentManagerDirectory.searchValue?.value);
    const moveTo = useSelector((state: RootState) => state.contentManagerDirectory.moveTo);

    const dispatch = useDispatch();

    const onBreadcrumbItemClicked = (ev: React.MouseEvent<HTMLElement>, item: IBreadcrumbItem): void => {
        dispatch(removeDirectoriesByIndex(item));
        dispatch(toggleContentManagerReload());
        dispatch(setContentManagerSearchValue(""));
        dispatch(setContentManagerFilterValue(""));
        ev?.preventDefault();
        ev?.stopPropagation();
    };

    const onBreadcrumbResetClicked = (ev: React.MouseEvent<HTMLElement>, item: IBreadcrumbItem): void => {
        dispatch(resetWorkingDirectories());
        dispatch(resetContentManagerDirectory());
        dispatch(setContentManagerSearchValue(""));
        dispatch(setContentManagerFilterValue(""));
        ev.preventDefault();
        ev.stopPropagation();
    };

    React.useEffect(() => {
        setWorkingDirectoriesBreadcrumb();
    }, [workingDirectories, searchValue]);

    const setWorkingDirectoriesBreadcrumb = () => {
        let items: IBreadcrumbItem[] = [{ text: "Home", key: "Home", href: "#", onClick: onBreadcrumbResetClicked }];

        if (workingDirectories.some(x => x.sharePointListId || x.type === ComponentTypes.SharePointDocuments || x.type === ComponentTypes.TessAccount || x.type === ComponentTypes.TessProject)) {
            workingDirectories.forEach((directory, index: number) => {
                if (directory.sharePointListId || directory.type === ComponentTypes.SharePointDocuments || directory.type === ComponentTypes.TessAccount || directory.type === ComponentTypes.TessProject) {
                    if (index < workingDirectories.length - 1 || searchValue) {
                        items.push({
                            text: directory.displayName,
                            key: index.toString(),
                            href: "#",
                            onClick: onBreadcrumbItemClicked,
                            onRender: (item?: IBreadcrumbItem, defaultRender?: (item?: IBreadcrumbItem) => JSX.Element | null) => {
                                return <DroppableBreadcrumbItem
                                    item={item}
                                    directory={directory}
                                    onNavigate={onBreadcrumbItemClicked}>
                                    {defaultRender!(item)}
                                </DroppableBreadcrumbItem>;
                            }
                        });
                    } else {
                        items.push({ text: directory.displayName, key: index.toString() });
                    }
                }
            });
        } else if (workingDirectories.some(x => x.type === ComponentTypes.Exchange)) {
            workingDirectories.forEach((directory, index: number) => {
                items.push({ text: directory.displayName, key: index.toString() });
            });
        }

        if (searchValue) {
            items.push({ text: `Search: ${searchValue}`, key: "search" });
        }

        setBreadCrumbItems(items);
    }

    return (
        <>
            {moveTo.moveToEnabled && (moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails || moveTo.mode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails) &&
                <div style={{ paddingTop: "5px", paddingRight: "5px" }}>
                    <MessageBar
                        messageBarType={MessageBarType.warning}
                        isMultiline={false}
                        dismissButtonAriaLabel="Close"
                        actions={
                            <div>
                                <MessageBarButton onClick={() => {
                                    dispatch(setContentManagerMoveTo({
                                        moveToEnabled: false,
                                        workingDirectories: [],
                                        selectedFiles: [],
                                        siteUrl: "",
                                        sourceServerRelPath: "",
                                    }));
                                    dispatch(setContentManagerExchange({ selectedFolderId: "", selectedFolderName: "", selectedAccount: "" }));

                                    dispatch(setFiles([]));
                                }}>{languageStrings.Reset}</MessageBarButton>
                            </div>
                        }
                    >
                        {languageStrings.AttentionSaveModeActive}
                    </MessageBar>
                </div>}
            <Breadcrumb
                items={breadCrumbItems}
                ariaLabel="With last item rendered as heading"
                overflowAriaLabel="More links"
            />
        </>
    )
};